import React, { useState } from 'react';
import { AlertCircle, Loader2, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../../utils/cn';
import { setVisitorAuthorizerFlag } from '../../../services/visitorAuthorizerApi';
import {
  canAuthorizeVisitors,
  visitorAuthorizerRelationLabel,
} from '../../../pages/visitorAuthorizer';

/**
 * One person's exception to "quem pode liberar visitantes".
 *
 * Shown ONLY when the customer has the restriction switched on. With it off everybody can already
 * authorize visitors, and a toggle promising a permission that is not being withheld reads as
 * either broken or as a second, hidden gate.
 *
 * It GRANTS and never revokes: an exception list, not a per-person switch everybody also has to
 * pass. So a person whose vínculo is already allowed is told that, rather than being shown an
 * unticked box that implies they cannot.
 *
 * The exception exists because a condomínio that restricts to proprietários still has the one
 * inquilino who manages the place — and the alternative is lying about their vínculo, which
 * corrupts the record everything else depends on.
 */

interface Props {
  customerId: string;
  personId: string;
  /** The person's relation to their unit or departamento. */
  relation: string;
  isEmpresa: boolean;
  /** The customer's choice. */
  controlEnabled: boolean;
  allowedRelations: string[];
  /** Whether the flag is currently set on the person's assignment. */
  hasFlag: boolean;
  readOnly?: boolean;
  darkMode?: boolean;
  onRefresh: () => void;
}

const VisitorAuthorizerPersonToggle: React.FC<Props> = ({
  customerId,
  personId,
  relation,
  isEmpresa,
  controlEnabled,
  allowedRelations,
  hasFlag,
  readOnly,
  darkMode,
  onRefresh,
}) => {
  const [saving, setSaving] = useState(false);

  // Nothing is being withheld, so there is no exception to make.
  if (!controlEnabled || allowedRelations.length === 0) return null;

  const allowedByRelation = canAuthorizeVisitors(
    { enabled: true, relations: allowedRelations },
    relation,
    false,
  );
  const relationLabel = relation
    ? visitorAuthorizerRelationLabel(relation, isEmpresa)
    : 'sem vínculo definido';

  const save = async (next: boolean) => {
    setSaving(true);
    try {
      await setVisitorAuthorizerFlag(customerId, personId, next);
      toast.success(next ? 'Pode liberar visitantes' : 'Permissão removida');
      onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar a permissão');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <UserCheck className={cn('w-4 h-4', darkMode ? 'text-gray-400' : 'text-gray-500')} />
            <span
              className={cn(
                'text-[10px] font-semibold tracking-widest uppercase',
                darkMode ? 'text-gray-400' : 'text-gray-500',
              )}
            >
              Liberar visitantes
            </span>
          </div>

          {allowedByRelation ? (
            // Already allowed by their vínculo. Said plainly instead of showing an unticked box,
            // which would imply the opposite.
            <p className={cn('text-xs mt-1.5', darkMode ? 'text-gray-300' : 'text-gray-600')}>
              Já pode liberar visitantes porque o vínculo é <strong>{relationLabel}</strong>, que
              este local autoriza.
            </p>
          ) : (
            <p className={cn('text-xs mt-1.5', darkMode ? 'text-gray-300' : 'text-gray-600')}>
              O vínculo <strong>{relationLabel}</strong> não autoriza liberar visitantes neste
              local. Marque abaixo para abrir exceção para esta pessoa.
            </p>
          )}

          {hasFlag && !allowedByRelation && (
            <p
              className={cn(
                'flex items-start gap-1.5 text-[11px] leading-snug mt-1.5',
                darkMode ? 'text-amber-400' : 'text-amber-700',
              )}
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
              Liberação individual ativa — vale mesmo que a regra do local mude.
            </p>
          )}
        </div>

        {!readOnly && !allowedByRelation && (
          <label className="flex items-center gap-2 shrink-0">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
            <input
              type="checkbox"
              checked={hasFlag}
              disabled={saving}
              onChange={(event) => void save(event.target.checked)}
              className="rounded border-gray-300"
              aria-label="Pode liberar visitantes"
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default VisitorAuthorizerPersonToggle;
